'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import gsap from 'gsap';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import styles from './BatterySection.module.css';

const BG_IMAGE_DESKTOP = 'web_v2/ring-pro/landing-page/battery/desktop.png';
const BG_IMAGE_MOBILE = 'web_v2/ring-pro/landing-page/battery/mobile.png';
const TARGET_VALUE = 15;
const PHASE2_VALUE = 45;

/* ── Arc geometry ──────────────────────────────────────── */

const STROKE_WIDTH = 8;

const RADIUS = 330;
const SIZE = (RADIUS + STROKE_WIDTH) * 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ARC_FRACTION = 1;
const ARC_OFFSET = CIRCUMFERENCE * (1 - ARC_FRACTION);
const RADIUS_MID = 300;

const CIRC_MID = 2 * Math.PI * RADIUS_MID;

const CX = SIZE / 2;
const CY = SIZE / 2;

/* ── Component ─────────────────────────────────────────── */

const BatterySection = () => {
  const t = useTranslations('ring-pro');
  const sectionRef = useRef<HTMLElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const displayNumber = useRef<HTMLSpanElement>(null);
  const uptoLabelRef = useRef<HTMLParagraphElement>(null);
  const counterRef = useRef<HTMLParagraphElement>(null);

  const bgWrapperRef = useRef<HTMLDivElement>(null);
  const glassCircleRef = useRef<HTMLDivElement>(null);
  const outerArcRef = useRef<SVGCircleElement>(null);
  const midArcRef = useRef<SVGCircleElement>(null);

  const daysLabelRef = useRef<HTMLParagraphElement>(null);
  const chargingLabelRef = useRef<HTMLParagraphElement>(null);

  const gsapCtx = useGsapContext();
  const phase1TweenRef = useRef<gsap.core.Tween | null>(null);
  const phase1LabelsRef = useRef<gsap.core.Tween[]>([]);

  /* ── Phase 1: viewport-enter animation ───────────────── */

  const completePhase1 = () => {
    if (phase1TweenRef.current) {
      phase1TweenRef.current.progress(1);
      phase1TweenRef.current = null;
    }
    phase1LabelsRef.current.forEach((t) => t.progress(1));
    phase1LabelsRef.current = [];
    setShowLabels(true);
    if (displayNumber.current) {
      displayNumber.current.textContent = `${TARGET_VALUE} `;
    }
    if (counterRef.current) {
      counterRef.current.style.filter = 'blur(0px)';
    }
  };

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isActive) return;
    if (displayNumber.current) displayNumber.current.textContent = '0 ';
    const proxy = { value: 0 };
    const tween = gsap.fromTo(proxy, { value: 0 }, {
      value: TARGET_VALUE,
      duration: 1.5,
      delay: 0.2,
      ease: 'power2.out',
      onUpdate: () => {
        const v = proxy.value;
        if (displayNumber.current) {
          displayNumber.current.textContent = `${Math.round(v)} `;
        }
        if (counterRef.current) {
          let blur: number;
          if (v <= 8) blur = 4 - (v / 8) * 3;
          else blur = 1 - (v - 8) / 2;
          counterRef.current.style.filter = `blur(${Math.max(0, blur)}px)`;
        }
      },
      onComplete: () => {
        phase1TweenRef.current = null;
        setShowLabels(true);
      },
    });
    phase1TweenRef.current = tween;
    return () => {
      tween.kill();
    };
  }, [isActive]);

  useEffect(() => {
    if (!showLabels) return;
    const tweens: gsap.core.Tween[] = [];
    if (uptoLabelRef.current) {
      tweens.push(gsap.fromTo(
        uptoLabelRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' },
      ));
    }
    if (daysLabelRef.current) {
      tweens.push(gsap.fromTo(
        daysLabelRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' },
      ));
    }
    phase1LabelsRef.current = tweens;
  }, [showLabels]);

  /* ── Phase 2: scroll-driven transition ───────────────── */

  useIsomorphicLayoutEffect(() => {
    if (!scrollWrapperRef.current) return;

    gsapCtx.add(() => {
      const wrapper = scrollWrapperRef.current;
      if (!wrapper) return;

      const counterProxy = { value: TARGET_VALUE };

      let phase1Completed = false;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.5,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            if (!phase1Completed && self.progress > 0) {
              phase1Completed = true;
              completePhase1();
            }
          },
        },
      });

      if (outerArcRef.current) {
        tl.set(
          outerArcRef.current,
          {
            animation: 'none',
            strokeDashoffset: ARC_OFFSET,
          },
          0,
        );
      }

      if (bgWrapperRef.current) {
        tl.to(
          bgWrapperRef.current,
          { opacity: 0, duration: 0.3, ease: 'none' },
          0,
        );
      }
      if (glassCircleRef.current) {
        tl.to(
          glassCircleRef.current,
          { opacity: 0, duration: 0.3, ease: 'none' },
          0,
        );
      }

      if (midArcRef.current) {
        tl.to(
          midArcRef.current,
          {
            attr: { 'stroke-dashoffset': 0 },
            duration: 0.5,
            ease: 'power2.out',
          },
          0.2,
        );
      }

      tl.fromTo(
        counterProxy,
        { value: TARGET_VALUE },
        {
          value: PHASE2_VALUE,
          duration: 0.4,
          ease: 'none',
          immediateRender: false,
          onUpdate: () => {
            if (displayNumber.current) {
              displayNumber.current.textContent = `${Math.round(
                counterProxy.value,
              )} `;
            }
          },
        },
        0.1,
      );

      if (daysLabelRef.current) {
        tl.set(daysLabelRef.current, { opacity: 1 }, 0);
        tl.to(
          daysLabelRef.current,
          { opacity: 0, duration: 0.2, ease: 'none' },
          0.2,
        );
      }

      if (uptoLabelRef.current) {
        tl.set(uptoLabelRef.current, { opacity: 1 }, 0);
      }

      if (chargingLabelRef.current) {
        tl.to(
          chargingLabelRef.current,
          { opacity: 1, duration: 0.2, ease: 'none' },
          0.4,
        );
      }

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });
  }, [gsapCtx]);

  /* ── Render ──────────────────────────────────────────── */

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.scrollWrapper} ref={scrollWrapperRef}>
        <div className={styles.stickyContainer}>
          <div className={styles.bgWrapper} ref={bgWrapperRef}>
            <CustomImage
              className={styles.desktopBgImage}
              src={BG_IMAGE_DESKTOP}
              alt=""
              fill
              sizes="100vw"
              priority={false}
            />
            <CustomImage
              className={styles.mobileBgImage}
              src={BG_IMAGE_MOBILE}
              alt=""
              fill
              sizes="100vw"
              priority={false}
            />
          </div>

          <div className={styles.dialContainer}>
            <div className={styles.glassCircle} ref={glassCircleRef} />

            <svg className={styles.arcSvg} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
              <circle
                className={styles.arcCircle}
                ref={outerArcRef}
                cx={CX}
                cy={CY}
                r={RADIUS}
                data-active={isActive ? 'true' : undefined}
              />

              <circle
                className={styles.midArc}
                ref={midArcRef}
                cx={CX}
                cy={CY}
                r={RADIUS_MID}
                strokeDasharray={CIRC_MID}
                strokeDashoffset={CIRC_MID}
              />
            </svg>

            <div className={styles.textContent}>
              <p className={styles.uptoLabel} ref={uptoLabelRef}>{t('ring-pro.battery.upto')}</p>

              <p className={styles.counter} ref={counterRef}>
                <span ref={displayNumber}>0 </span>
              </p>

              <div className={styles.labelStack}>
                <p className={styles.daysLabel} ref={daysLabelRef}>{t('ring-pro.battery.daysBatteryLife')}</p>
                <p className={styles.chargingLabel} ref={chargingLabelRef}>
                  {t('ring-pro.battery.days')}
                  <br />
                  {t('ring-pro.battery.withProCharger')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BatterySection;
